import { useState } from 'react';
import { X, Camera, Link2, FileText, Edit } from 'lucide-react';
import { PhotoRecipeForm } from './PhotoRecipeForm';
import { ManualRecipeForm } from './ManualRecipeForm';
import { UrlRecipeForm } from './UrlRecipeForm';
import { TextRecipeForm } from './TextRecipeForm';
import { apiPost } from '../utils/apiClient';
import { hasTime, toMinutes } from '../utils/recipeTime';
import { showGlobalError } from '../utils/errorDisplay';

// Imported recipes often carry dozens of SEO-junk tags. Silently keep the first 10 (deduped,
// non-empty) rather than blocking the import — the backend caps at 30, but 10 stays tidy.
const MAX_TAGS = 10;

// Meaningful end-user message from an apiClient error ({ status, message, details[] }). Used
// only for GENUINE save failures now that tag overflow is trimmed rather than rejected.
const describeSaveError = (e) => {
  const details = Array.isArray(e?.details) ? e.details.map((d) => d?.message).filter(Boolean) : [];
  if (details.length) return `Couldn't save recipe: ${details.join('; ')}`;
  if (e?.message) return `Couldn't save recipe: ${e.message}`;
  return "Couldn't save recipe. Please try again.";
};

export function ImportRecipeModal({ isOpen, onClose, userId = 'user-default', onRecipeSaved }) {
  const [isPhotoFormOpen, setIsPhotoFormOpen] = useState(false);
  const [isManualFormOpen, setIsManualFormOpen] = useState(false);
  const [isUrlFormOpen, setIsUrlFormOpen] = useState(false);
  const [isTextFormOpen, setIsTextFormOpen] = useState(false);
  const [importedRecipeData, setImportedRecipeData] = useState(null);

  // Normalize a recipe object to backend shape
  const normalizeForBackend = (r) => {
    // No 'lunch' fallback — ManualRecipeForm makes meal type mandatory, so an empty value
    // here would mean validation was bypassed. The backend schema permits '' rather than
    // silently mislabelling the recipe.
    const lowerMealType = String(r.mealType || '').toLowerCase();

    // Ingredients may be strings (photo form) or objects with quantity string
    const ingList = Array.isArray(r.ingredients) ? r.ingredients : [];
    const toNumberFromTokens = (tokens) => {
      let total = 0;
      for (const t of tokens) {
        if (/^\d+\/\d+$/.test(t)) {
          const [n, d] = t.split('/').map(Number);
          if (!isNaN(n) && !isNaN(d) && d !== 0) total += n / d;
        } else if (/^\d+(?:\.\d+)?$/.test(t)) {
          const n = Number(t);
          if (!isNaN(n)) total += n;
        } else {
          break;
        }
      }
      return total || null;
    };
    const splitQuantityUnit = (q) => {
      if (typeof q !== 'string') return { quantity: typeof q === 'number' ? q : null, unit: '' };
      const trimmed = q.trim();
      if (!trimmed) return { quantity: null, unit: '' };
      const tokens = trimmed.split(/\s+/);
      const qty = toNumberFromTokens(tokens);
      // Determine how many numeric tokens were consumed
      let consumed = 0;
      for (const t of tokens) {
        if (/^\d+\/\d+$/.test(t) || /^\d+(?:\.\d+)?$/.test(t)) consumed++;
        else break;
      }
      const unit = tokens.slice(consumed).join(' ');
      return { quantity: qty, unit };
    };

    const ingredients = ingList.map((ing) => {
      if (typeof ing === 'string') {
          const m = ing.match(/^([\d\s/.,]+.*?)\s+(.+)$/);
        if (m) {
          const parts = splitQuantityUnit(m[1]);
          return { name: m[2].trim(), quantity: parts.quantity ?? 0, unit: parts.unit || '', category: undefined };
        }
        return { name: ing.trim(), quantity: 0, unit: '', category: undefined };
      }
      const { quantity: qRaw, name, category } = ing || {};
      const parts = splitQuantityUnit(typeof qRaw === 'string' ? qRaw : String(qRaw ?? ''));
      return { name: String(name || '').trim(), quantity: parts.quantity ?? 0, unit: parts.unit || '', category };
    });

    // Normalize method to array of strings
    const method = Array.isArray(r.method)
      ? r.method.map((s) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean)
      : (typeof r.method === 'string' ? r.method.split(/\r?\n/).map(s => s.trim()).filter(Boolean) : []);

    // Keep only the first 10 non-empty, unique tags so tag-heavy imports still save cleanly.
    // Split on ',' and ';' as well: some sites (Drupal-based ones like kingarthurbaking.com)
    // delimit JSON-LD keywords with ';;', which would otherwise arrive as one giant tag. The
    // backend normalizes this too — this is a safety net for anything the OCR path invents.
    const cleanedTags = [...new Set(
      (Array.isArray(r.tags) ? r.tags : [])
        .flatMap((t) => (typeof t === 'string' ? t.split(/[;,]+/) : []))
        .map((t) => t.trim())
        .filter(Boolean)
    )].slice(0, MAX_TAGS);

    // Map photo/image field; drop base64 data URIs to avoid oversized payloads
    const rawImage = r.image || r.photo || '';
    const image = (typeof rawImage === 'string' && rawImage.startsWith('data:')) ? '' : rawImage;
    const imagePath = typeof r.imagePath === 'string' ? r.imagePath : '';
    const rawImageThumb = r.imageThumb || r.thumbnail || r.thumbUrl || '';
    const imageThumb = (typeof rawImageThumb === 'string' && rawImageThumb.startsWith('data:')) ? '' : rawImageThumb;
    const imageThumbPath =
      typeof r.imageThumbPath === 'string'
        ? r.imageThumbPath
        : (typeof r.thumbnailPath === 'string' ? r.thumbnailPath : (typeof r.thumbPath === 'string' ? r.thumbPath : ''));

    const rawNutrition = r.nutrition || {};
    const hasAnyNutrition =
      rawNutrition.calories !== undefined ||
      rawNutrition.protein !== undefined ||
      rawNutrition.carbs !== undefined ||
      rawNutrition.fat !== undefined;

    const nutrition = hasAnyNutrition
      ? {
          calories: Number(rawNutrition.calories) || 0,
          protein: Number(rawNutrition.protein) || 0,
          carbs: Number(rawNutrition.carbs) || 0,
          fat: Number(rawNutrition.fat) || 0,
        }
      : undefined;

    return {
      name: r.name || '',
      servings: Number(r.servings) || 1,
      // '' (not 0) when blank, so a missing time stays distinguishable from a real zero
      prepTime: toMinutes(r.prepTime) ?? '',
      cookTime: toMinutes(r.cookTime) ?? '',
      // Dropped only once a COMPLETE breakdown supersedes it. Kept alongside a half
      // breakdown, where it's still the better estimate of the real total.
      totalTime: (hasTime(toMinutes(r.prepTime)) && hasTime(toMinutes(r.cookTime)))
        ? ''
        : (toMinutes(r.totalTime) ?? ''),
      source: r.source || '',
      ingredients,
      method,
      image,
      imagePath,
      imageThumb,
      imageThumbPath,
      tags: cleanedTags,
      mealType: lowerMealType,
      nutrition,
    };
  };

  const handleTakePhoto = () => {
    setIsPhotoFormOpen(true);
    onClose(); // Close the import modal
  };

  const handlePhotoRecipeSave = async (recipe) => {
    try {
      const body = normalizeForBackend(recipe);
      await apiPost('/recipes', body);
      if (typeof onRecipeSaved === 'function') onRecipeSaved();
    } catch (e) {
      console.warn('Failed to save recipe:', e);
      showGlobalError(describeSaveError(e));
    } finally {
      // Close stays controlled by child; no-op here
    }
  };

  const handlePasteLink = () => {
    setIsUrlFormOpen(true);
    onClose();
  };

  const handleUrlImportSuccess = (recipe) => {
    // Store the imported recipe data and open the manual form for editing
    setImportedRecipeData(recipe);
    setIsManualFormOpen(true);
  };

  const handlePasteText = () => {
    setIsTextFormOpen(true);
    onClose();
  };

  const handleTextImportSuccess = (recipe) => {
    // Store the imported recipe data and open the manual form for editing
    setImportedRecipeData(recipe);
    setIsManualFormOpen(true);
  };

  const handleEnterManually = () => {
    setImportedRecipeData(null); // Clear any imported data
    setIsManualFormOpen(true);
    onClose(); // Close the import modal
  };

  const handleManualRecipeSave = async (recipe) => {
    try {
      const body = normalizeForBackend(recipe);
      await apiPost('/recipes', body);
      if (typeof onRecipeSaved === 'function') onRecipeSaved();
    } catch (e) {
      console.warn('Failed to save manual recipe:', e);
      showGlobalError(describeSaveError(e));
    } finally {
      setImportedRecipeData(null);
    }
  };

  const handleManualFormClose = () => {
    setIsManualFormOpen(false);
    setImportedRecipeData(null); // Clear imported data when closing without saving
  };

  const options = [
    {
      icon: Camera,
      label: 'Take a Photo',
      description: 'Scan a recipe from a book or card',
      onClick: handleTakePhoto,
      color: '#7B9EC6',
    },
    {
      icon: Link2,
      label: 'Paste a Link',
      description: 'Import from a recipe website',
      onClick: handlePasteLink,
      color: '#7B9EC6',
    },
    {
      icon: FileText,
      label: 'Paste Text',
      description: 'Copy and paste recipe text',
      onClick: handlePasteText,
      color: '#7B9EC6',
    },
    {
      icon: Edit,
      label: 'Enter Manually',
      description: 'Type in the recipe yourself',
      onClick: handleEnterManually,
      color: '#7B9EC6',
    },
  ];

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/10 flex items-end justify-center z-50">
          {/* Modal Sheet */}
          <div className="bg-white rounded-t-3xl w-full max-w-md shadow-2xl animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-xl">Import Recipe</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Options */}
            <div className="p-4 pb-8">
              {options.map((option) => (
                <button
                  key={option.label}
                  onClick={option.onClick}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-colors mb-2"
                >
                  <div className="w-12 h-12 rounded-full bg-[#7B9EC6]/10 flex items-center justify-center flex-shrink-0">
                    <option.icon className="w-6 h-6 text-[#7B9EC6]" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-gray-900">{option.label}</div>
                    <div className="text-sm text-[#757575]">{option.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Photo Recipe Form */}
      <PhotoRecipeForm
        isOpen={isPhotoFormOpen}
        onSave={handlePhotoRecipeSave}
        onClose={() => setIsPhotoFormOpen(false)}
      />

      {/* URL Recipe Form */}
      <UrlRecipeForm
        isOpen={isUrlFormOpen}
        onImportSuccess={handleUrlImportSuccess}
        onClose={() => setIsUrlFormOpen(false)}
        userId={userId}
        useMockData={false}
      />

      {/* Text Recipe Form */}
      <TextRecipeForm
        isOpen={isTextFormOpen}
        onImportSuccess={handleTextImportSuccess}
        onClose={() => setIsTextFormOpen(false)}
        userId={userId}
      />

      {/* Manual Recipe Form - Now accepts imported data */}
      <ManualRecipeForm
        isOpen={isManualFormOpen}
        onSave={handleManualRecipeSave}
        onClose={handleManualFormClose}
        existingRecipe={importedRecipeData}
        isImported={!!importedRecipeData}
        userId={userId}
      />
    </>
  );
}
